import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Calendar, Plus, Search, Edit2, Trash2 } from 'lucide-react';
import axios from 'axios';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import Table from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface Schedule {
  _id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

const SchedulesPage: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_URL}/schedules`, axiosConfig);
      setSchedules(response.data.schedules || []);
    } catch (error) {
      toast.error('Failed to fetch schedules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handleOpenModal = (schedule?: Schedule) => {
    if (schedule) {
      setEditingSchedule(schedule);
      setFormData({ name: schedule.name, description: schedule.description || '' });
    } else {
      setEditingSchedule(null);
      setFormData({ name: '', description: '' });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingSchedule(null);
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Schedule name is required');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
      if (editingSchedule) {
        await axios.put(`${API_URL}/schedules/${editingSchedule._id}`, formData, axiosConfig);
        toast.success('Schedule updated successfully');
      } else {
        await axios.post(`${API_URL}/schedules`, formData, axiosConfig);
        toast.success('Schedule created successfully');
      }
      handleCloseModal();
      fetchSchedules();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save schedule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;

    try {
      const token = localStorage.getItem('token');
      const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_URL}/schedules/${id}`, axiosConfig);
      toast.success('Schedule deleted successfully');
      fetchSchedules();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete schedule');
    }
  };

  const filteredSchedules = schedules.filter(schedule =>
    schedule.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { header: 'Name', accessor: (row: Schedule) => row.name },
    { header: 'Description', accessor: (row: Schedule) => row.description || '-' },
    {
      header: 'Actions',
      accessor: (row: Schedule) => (
        <div className="flex gap-0.5">
          <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" icon={<Edit2 className="w-4 h-4" />} onClick={() => handleOpenModal(row)} />
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" icon={<Trash2 className="w-4 h-4" />} onClick={() => handleDelete(row._id)} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Schedules</h1>
          <p className="mt-2 text-sm text-gray-600">Manage schedule information</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add Schedule</Button>
      </motion.div>

      <Card padding="md">
        <Input placeholder="Search schedules..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} icon={<Search className="w-5 h-5" />} fullWidth />
      </Card>

      <Card padding="none">
        {loading ? (
          <div className="flex justify-center items-center py-12"><LoadingSpinner size="lg" /></div>
        ) : filteredSchedules.length > 0 ? (
          <Table data={filteredSchedules} columns={columns} emptyMessage="No schedules found" />
        ) : (
          <div className="px-6 py-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No schedules yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first schedule.</p>
            <div className="mt-6"><Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add Schedule</Button></div>
          </div>
        )}
      </Card>

      <Modal isOpen={modalOpen} onClose={handleCloseModal} title={editingSchedule ? 'Edit Schedule' : 'Add New Schedule'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter schedule name" required fullWidth />
          <Input label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Enter description (optional)" fullWidth />
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={submitting}>Cancel</Button>
            <Button type="submit" loading={submitting}>{editingSchedule ? 'Update Schedule' : 'Create Schedule'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SchedulesPage;

